import { useMemo } from 'react';
import {
  CartesianGrid,
  Line,
  LineChart,
  ReferenceDot,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from 'recharts';
import {
  abbreviateNumber,
  formatMoney,
  formatNumber
} from '@blisspointmedia/bpm-types/dist/formatUtils';
import { ChartContainer } from '@Components/ChartContainer';
import {
  CARTESIAN_GRID_STROKE,
  CARTESIAN_GRID_STROKE_WIDTH,
  TICK_STYLE
} from '@Components/Charts/ChartConstants';
import { ChartTooltip } from '@Components/Charts/ChartTooltip';
import { InfoTooltip } from '@Components/InfoTooltip';
import { Placement, Size } from '@Components/designConstants';
import { Brand50, Neutral600 } from '../../utils/colors';
import { getNiceTicks } from './youtube-planning-utils';
import './SpendVsChart.scss';

interface SpendVsChartProps {
  variableName: string;
  data: { val: number; spend: number }[];
  suggestedBudget?: number;
}

interface SpendVsTooltipProps {
  payload?: { payload: { spend: number; val: number } }[];
  variableName: string;
}

const SpendVsTooltip = ({ payload, variableName }: SpendVsTooltipProps) => {
  const tooltipData = payload?.[0]?.payload;
  if (!tooltipData) return null;

  const headerLabel = `Spend: ${formatMoney(tooltipData.spend)}`;
  const tooltipItems = [
    {
      label: variableName,
      value: formatNumber(parseInt(String(tooltipData.val), 10)),
      color: Brand50
    }
  ];

  return <ChartTooltip headerLabel={headerLabel} items={tooltipItems} />;
};

// The empty case is branched here, in a component with no hooks, so SpendVsLineChart only ever
// mounts with a non-empty series -- its axis domains derive from Math.max over the data, which is
// -Infinity for an empty array and renders a broken axis rather than an empty chart.
export const SpendVsChart = ({ variableName, data, suggestedBudget }: SpendVsChartProps) => (
  <div className='youtube-spend-vs-chart'>
    <ChartContainer
      title={
        <div className='youtube-spend-vs-chart-title'>
          Spend vs {variableName}
          <InfoTooltip size={Size.SM} placement={Placement.TOP_END}>
            Highest spend level reflects the maximum available. Estimates are capped at each
            plan&apos;s limit and vary by query and targeting.
          </InfoTooltip>
        </div>
      }
    >
      {data.length === 0 ? (
        <div className='youtube-spend-vs-chart-empty'>
          No {variableName} forecast is available for this plan.
        </div>
      ) : (
        <SpendVsLineChart
          variableName={variableName}
          data={data}
          suggestedBudget={suggestedBudget}
        />
      )}
    </ChartContainer>
  </div>
);

const SpendVsLineChart = ({ variableName, data, suggestedBudget }: SpendVsChartProps) => {
  const sortedData = useMemo(() => [...data].sort((a, b) => a.spend - b.spend), [data]);

  const maxVal = useMemo(() => Math.max(...sortedData.map(d => d.val)), [sortedData]);

  const suggestedBudgetYVal = useMemo(
    () => sortedData.find(point => point.spend === suggestedBudget)?.val,
    [suggestedBudget, sortedData]
  );

  const spendTicks = useMemo(
    () => getNiceTicks(0, sortedData[sortedData.length - 1].spend, 5),
    [sortedData]
  );

  const valTicks = useMemo(() => getNiceTicks(0, maxVal, 3), [maxVal]);

  return (
    <ResponsiveContainer width='100%' height={240}>
      <LineChart
        data={sortedData}
        margin={{
          top: 0,
          right: 48,
          left: -20,
          bottom: -12
        }}
      >
        <CartesianGrid
          stroke={CARTESIAN_GRID_STROKE}
          strokeWidth={CARTESIAN_GRID_STROKE_WIDTH}
          vertical={false}
        />
        <XAxis
          dataKey='spend'
          tick={TICK_STYLE}
          ticks={spendTicks}
          tickFormatter={val => `$${abbreviateNumber(val)}`}
          scale='linear'
          domain={[0, spendTicks[spendTicks.length - 1]]}
        />
        <YAxis
          tick={TICK_STYLE}
          ticks={valTicks}
          tickLine={false}
          tickFormatter={val => `${abbreviateNumber(val)} `}
          axisLine={false}
          width={75}
          type='number'
          domain={[0, maxVal]}
        />
        <ReferenceLine
          x={suggestedBudget}
          label={{
            position: 'insideTopLeft',
            value: 'Budget'
          }}
          stroke={Neutral600}
        />
        <ReferenceDot
          x={suggestedBudget}
          y={suggestedBudgetYVal}
          r={6}
          fill={Brand50}
          stroke='none'
        />
        <Tooltip content={<SpendVsTooltip variableName={variableName} />} />
        <Line
          type='monotone'
          dataKey='val'
          dot={false}
          isAnimationActive={false}
          stroke={Brand50}
          strokeWidth={3}
        />
      </LineChart>
    </ResponsiveContainer>
  );
};
